import type { MatrixPage, RunCost } from "@/types";
import { formatUsd } from "@/utils/format";
import { specKey } from "./teSpec";

/** Calls that count 0 in the total: no price, or no tokens reported. */
function uncounted(cost: RunCost): number {
  return cost.unpriced + (cost.unreported ?? 0);
}

export function sumCosts(costs: readonly (RunCost | null | undefined)[]): RunCost | null {
  const known = costs.filter((c): c is RunCost => c != null);
  if (known.length === 0) return null;
  return known.reduce<RunCost>(
    (sum, c) => ({
      usd: sum.usd + c.usd,
      replay_usd: (sum.replay_usd ?? 0) + (c.replay_usd ?? 0),
      judge_usd: (sum.judge_usd ?? 0) + (c.judge_usd ?? 0),
      events: sum.events + c.events,
      unpriced: sum.unpriced + c.unpriced,
      unreported: (sum.unreported ?? 0) + (c.unreported ?? 0),
    }),
    { usd: 0, replay_usd: 0, judge_usd: 0, events: 0, unpriced: 0, unreported: 0 },
  );
}

/** Every run of one TE on the page — re-runs included, since each was paid for. */
export function teCost(page: MatrixPage, key: string): RunCost | null {
  return sumCosts(page.runs.filter((r) => specKey(r.te_spec ?? {}) === key).map((r) => r.cost));
}

/** "$12.5207", or "≥$12.5207" when some calls could not be priced. A run with no record is a dash. */
export function formatCost(cost: RunCost | null | undefined): string {
  if (cost == null) return "—";
  return `${uncounted(cost) ? "≥" : ""}${formatUsd(cost.usd)}`;
}

/** The breakdown for a tooltip. */
export function costTitle(cost: RunCost | null | undefined): string | undefined {
  if (cost == null) return undefined;
  const parts = [];
  if (cost.replay_usd != null || cost.judge_usd != null) {
    parts.push(`실행 ${formatUsd(cost.replay_usd ?? 0)} · 채점 ${formatUsd(cost.judge_usd ?? 0)}`);
  }
  parts.push(`LLM 호출 ${cost.events}건`);
  if (cost.unpriced) parts.push(`가격 없음 ${cost.unpriced}건`);
  if (cost.unreported) parts.push(`토큰 미보고 ${cost.unreported}건`);
  if (uncounted(cost)) parts.push("이 호출들은 0으로 계산되어 실제 비용은 더 큽니다");
  return parts.join("\n");
}
